/* eslint-disable no-unused-expressions */
import CONFIG from 'Config/settings';
import { ReduxStore } from 'Redux';
import { getFieldMessages, getFormValues } from 'Redux/forms/formsHelper';
import { t } from 'Utils/localization/i18next';
import validationMessage from '../validationMessage/validationMessage';
import {
  getFieldKeyByLabel,
  getPrimitiveRegExp,
  matchesFieldPolicy,
  resolveFieldPolicyRegExps
} from '../validationRules/validationRules';
import {
  dni,
  nif,
  passport,
  socialSecurityNumber
} from '../documentValidators/documentValidators';
import {
  addressLineWithApartmentNumber,
  notInjectionChars,
  notNumbersAndnotOnlySpaceNumberSpecialCharsAndNotInjectionChars,
  notOnlySpaceNumberSpecialCharsAndNotInjectionChars,
  notOnlySpaceSpecialCharsAndNotInjectionChars,
  phone,
  zipCode
} from '../addressValidators/addressValidators';
import { birthDate } from '../dateValidators/dateValidators';
import {
  PasswordErrorKeys,
  validateIdenticalChars,
  validateLowerCase,
  validateMaxLength,
  validateMinLength,
  validateNumbers,
  validateSpecialCharacters,
  validateUpperCase
} from '../password/password';

// Connects YAML validation names to validators and EDVE v2 rules.

const getEvaluationContext = formId => {
  if (!formId) {
    return null;
  }

  return formId.includes('signup') ? 'SIGN_UP' : 'PROFILE_UPDATE';
};

const alphanumericDashAndSpace = (value = '', label, { message }) => {
  if (!value.length) {
    return undefined;
  }
  const alphanumericDashAndSpaceRegExp = getPrimitiveRegExp(
    'alphanumeric_basic'
  );
  if (
    !alphanumericDashAndSpaceRegExp ||
    !alphanumericDashAndSpaceRegExp.test(value)
  ) {
    return validationMessage('alphanumericDashAndSpace', label, message);
  }
  return undefined;
};

const email = (value = '', label, { message }) => {
  if (!value.length) {
    return undefined;
  }
  const emailRegExp = getPrimitiveRegExp('email');
  if (!emailRegExp || !emailRegExp.test(value)) {
    return validationMessage('email', label, message);
  }
  return undefined;
};

const lengthRange = (value = '', label, { min, max, message }) => {
  if (!value.length) {
    return undefined;
  }
  const fieldKey = getFieldKeyByLabel(label);
  const resolvedPolicy = resolveFieldPolicyRegExps(
    fieldKey,
    CONFIG.COUNTRY.toUpperCase()
  );
  const minimumLength = resolvedPolicy?.constraints?.minLength ?? min;
  const maximumLength = resolvedPolicy?.constraints?.maxLength ?? max ?? min;

  if (value.length < minimumLength || value.length > maximumLength) {
    const range =
      minimumLength === maximumLength
        ? minimumLength
        : `${minimumLength}-${maximumLength}`;
    return validationMessage('lengthRange', label, message, { range });
  }
  return undefined;
};

const mandatory = (value = false, label, { message }, formId) => {
  const fieldKey = getFieldKeyByLabel(label);
  const resolvedPolicy = resolveFieldPolicyRegExps(
    fieldKey,
    CONFIG.COUNTRY.toUpperCase()
  );
  const evaluationContext = getEvaluationContext(formId);
  const requiredInContext = resolvedPolicy?.presence?.requiredIn?.includes(
    evaluationContext
  );
  const isRequired =
    resolvedPolicy?.presence && evaluationContext ? requiredInContext : true;
  const newValue = typeof value === 'string' ? value.trim() : value;

  if (isRequired && !newValue) {
    return validationMessage('mandatory', label, message);
  }
  return undefined;
};

const mandatoryIfOtherFieldMatchesRegex = (
  value = '',
  label,
  { fieldLabel, otherFieldName, otherFieldLabel, message, regex },
  formId,
  setFormFieldMessages
) => {
  const reduxState = ReduxStore.getState();
  const formValues = getFormValues(ReduxStore.getState(), formId);
  const otherFieldValue = formValues[otherFieldName];
  const isOtherFieldMatchesRegex = new RegExp(regex).test(
    otherFieldValue || ''
  );
  if (!value && isOtherFieldMatchesRegex) {
    return validationMessage('mandatory', label, message, {
      otherField: t(otherFieldLabel).toLocaleLowerCase(),
      field: t(fieldLabel || label).toLocaleLowerCase()
    });
  }

  // If the field currently being validated is valid, the depending field might
  // also be valid. Remove the validation message originating from this validator
  // from the depending field's list of validation messages.
  if (!setFormFieldMessages) {
    return undefined;
  }
  const currentMessages = getFieldMessages(reduxState, formId, otherFieldName);

  const originalMessage = validationMessage(
    'mandatory',
    otherFieldLabel,
    message,
    {
      otherField: t(label).toLocaleLowerCase()
    }
  )[0];

  const filteredMessages = currentMessages.filter(m => m !== originalMessage);
  setFormFieldMessages(formId, otherFieldName, filteredMessages);
  return undefined;
};

const mandatoryIfProfileFieldChanged = (
  value = false,
  label,
  { field, message },
  formId
) => {
  const reduxState = ReduxStore.getState();
  const formValues = getFormValues(reduxState, formId);
  const profileFieldValue =
    typeof reduxState.profile[field] === 'string'
      ? reduxState.profile[field].toLocaleLowerCase()
      : reduxState.profile[field];
  const formValue =
    typeof formValues[field] === 'string'
      ? formValues[field].toLocaleLowerCase()
      : formValues[field];
  const fieldChanged = profileFieldValue !== formValue;
  const newValue =
    typeof value === 'string' ? value.trim().toLowerCase() : value;
  if (fieldChanged && !newValue) {
    return validationMessage('mandatory', label, message);
  }
  return undefined;
};

const matchWithFormField = (value = '', label, { field, message }, formId) => {
  if (!value.length) {
    return undefined;
  }
  const reduxState = ReduxStore.getState();
  const formValues = getFormValues(reduxState, formId);

  if (formValues[field] && value !== formValues[field]) {
    return validationMessage('matchWithFormField', label, message);
  }
  return undefined;
};

const password = (value = '') => {
  const messages = [];
  const isNumbersOrSpecialCharacters =
    validateNumbers.check(value) || validateSpecialCharacters.check(value);
  !validateMinLength.check(value) && messages.push(PasswordErrorKeys[0]);
  !validateMaxLength(value) && messages.push(PasswordErrorKeys[0]);
  !validateIdenticalChars.check(value) && messages.push(PasswordErrorKeys[1]);
  !validateLowerCase.check(value) && messages.push(PasswordErrorKeys[2]);
  !validateUpperCase.check(value) && messages.push(PasswordErrorKeys[3]);
  !isNumbersOrSpecialCharacters && messages.push(PasswordErrorKeys[4]);
  return messages;
};

const groupRegex = (
  value = '',
  label,
  { fieldLabel, otherFieldName, otherFieldLabel, message, regex },
  formId,
  setFormFieldMessages
) => {
  const reduxState = ReduxStore.getState();
  const formValues = getFormValues(ReduxStore.getState(), formId);
  const otherFieldValue = formValues[otherFieldName];
  const otherFieldTranslationKey = otherFieldLabel || otherFieldName;
  const fieldTranslationKey = fieldLabel || label;
  const isGroupMatchesRegex = new RegExp(regex, 'i').test(
    `${value}${otherFieldValue || ''}`
  );
  if (isGroupMatchesRegex) {
    return validationMessage('invalid', label, message, {
      otherField: t(otherFieldTranslationKey).toLocaleLowerCase(),
      field: t(fieldTranslationKey).toLocaleLowerCase()
    });
  }

  // If the field currently being validated is valid, the depending field might
  // also be valid. Remove the validation message originating from this validator
  // from the depending field's list of validation messages.
  if (!setFormFieldMessages) {
    return undefined;
  }
  const currentMessages = getFieldMessages(reduxState, formId, otherFieldName);

  const originalMessage = validationMessage(
    'invalid',
    otherFieldTranslationKey,
    message,
    {
      otherField: t(label).toLocaleLowerCase()
    }
  )[0];

  const filteredMessages = currentMessages.filter(m => m !== originalMessage);
  setFormFieldMessages(formId, otherFieldName, filteredMessages);
  return undefined;
};

const regex = (value = '', label, { message }) => {
  if (!value.length) {
    return undefined;
  }
  const fieldKey = getFieldKeyByLabel(label);
  const resolvedPolicy = resolveFieldPolicyRegExps(
    fieldKey,
    CONFIG.COUNTRY.toUpperCase()
  );
  if (!resolvedPolicy || !matchesFieldPolicy(value, resolvedPolicy)) {
    return validationMessage('regex', label, message);
  }

  return undefined;
};

const childBirthDate = (
  value = '',
  label,
  { required, minAge = 0, maxAge = 18, format, childIndex },
  formId
) => {
  const { noOfChildren = 0 } = getFormValues(ReduxStore.getState(), formId);

  if (noOfChildren < childIndex + 1) {
    // Field should not be visible/validated
    // if "childIndex" exceeds "noOfChildren".
    return undefined;
  }
  if (required) {
    const msg = mandatory(value, label, {
      message: 'forms.validation.mandatoryGeneral'
    });
    if (msg) {
      return msg;
    }
  }
  if (format) {
    const msg = birthDate(value, label, {
      minAge,
      maxAge,
      format,
      message: 'forms.validation.childBirthdate'
    });
    if (msg) {
      return msg;
    }
  }
  return undefined;
};

export default {
  alphanumericDashAndSpace,
  addressLineWithApartmentNumber,
  birthDate,
  email,
  groupRegex,
  lengthRange,
  mandatory,
  mandatoryIfOtherFieldMatchesRegex,
  mandatoryIfProfileFieldChanged,
  matchWithFormField,
  notInjectionChars,
  notNumbersAndnotOnlySpaceNumberSpecialCharsAndNotInjectionChars,
  notOnlySpaceNumberSpecialCharsAndNotInjectionChars,
  notOnlySpaceSpecialCharsAndNotInjectionChars,
  password,
  regex,
  socialSecurityNumber,
  zipCode,
  phone,
  childBirthDate,
  nif,
  dni,
  passport
};
